import React, { useEffect, useState } from 'react';
import { Device } from '../../lib/types';
import { fosterSetup, isFosterOnRoster, type FosterHome } from '../../lib/fosterAdapters';
import { isFosterContactComplete } from '../../lib/utils';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../Modal/Modal';
import Button from '../Button/Button';
import NoticeIcon from '../NoticeIcon/NoticeIcon';
import styles from './RecallDeviceModal.module.scss';

interface RecallDeviceModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  /** The saved roster, to tell whether this foster's home is already on it. */
  homes: FosterHome[];
  /**
   * Whether `homes` has loaded. Required, not optional: the roster is fetched lazily — the Dashboard
   * starts it when this modal opens — so an empty `homes` means "not yet" as often as it means "no
   * saved homes", and the two lead to opposite answers about whether to offer the save.
   */
  homesLoaded: boolean;
  onRecall: (saveToRoster: boolean) => void;
}

export default function RecallDeviceModal({
  open,
  onClose,
  device,
  homes,
  homesLoaded,
  onRecall,
}: RecallDeviceModalProps) {
  // Opt-in: recall keeps behaving as it always has unless the rescue says otherwise.
  const [saveToRoster, setSaveToRoster] = useState(false);

  useEffect(() => {
    if (open) {
      setSaveToRoster(false);
    }
  }, [open]);

  if (!device) {
    return null;
  }

  const { holderName, holderFirstName, holderType, id, petName } = device;
  const isFoster = holderType === 'foster';
  const title = isFoster ? 'Recall device' : 'Unlink device';
  const petLabel = petName || 'this pet';

  /**
   * The same bar the assign picker sets (`isAssignable`), and for the same reason:
   * `createFosterTransfer` takes a non-optional email *and* phone, so a home saved with only one of
   * them can never be picked for the next placement — it would sit in the roster table filtered out
   * of every picker. A missing contact also blinds `isFosterOnRoster`, which matches on those two
   * fields alone, so without them we can't even tell a saved home from an unsaved one.
   *
   * Below the bar the backend resolves no contact snapshot, logs the miss, and tears the home down
   * regardless — so offering the save would promise something nothing downstream can deliver.
   */
  const canRecordHome = isFosterContactComplete(device);

  /**
   * Withheld until the roster is in. Offering it against an empty-because-unloaded roster shows the
   * checkbox and then takes it away a beat later for any foster already saved — and a control that
   * retracts itself reads as a bug, where appearing slightly late reads as loading.
   */
  const canSave = isFoster && canRecordHome && homesLoaded && !isFosterOnRoster(device, homes);
  // The rescue loses the home either way here; unlike the already-saved case, that's worth saying.
  const showNoContactNotice = isFoster && !!holderFirstName && !canRecordHome;
  // What the copy promises the home keeps, and what it warns still needs setting up.
  const { configured, missing } = fosterSetup({ hasSafeZone: device.safeZone, hasWifi: device.wifiConfigured });

  function handleConfirm() {
    onRecall(canSave && saveToRoster);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>{title}</ModalTitle>
      <ModalSubtitle>
        {isFoster ? (
          <>
            Recall <strong>{id}</strong>
            {petName ? ` (${petName})` : ''} from <strong>{holderName}</strong>? The foster will be notified to return
            the device.
          </>
        ) : (
          <>
            Unlink <strong>{id}</strong> from {petLabel}? The device will remain in your rescue&apos;s inventory.
          </>
        )}
      </ModalSubtitle>

      {canSave && (
        <>
          <label className={styles.check}>
            <input
              type="checkbox"
              className={styles.checkBox}
              checked={saveToRoster}
              onChange={(e) => setSaveToRoster(e.target.checked)}
            />
            <div>
              <div className={styles.checkTitle}>Save {holderFirstName} to your foster roster</div>
              <div className={styles.checkHint}>
                {holderFirstName}&apos;s foster household stays on file
                {configured ? ` with their ${configured}` : ''}. {petLabel} moves back to your rescue.
              </div>
            </div>
          </label>

          {missing && (
            <div className={`${styles.notice} ${styles.noticeInfo}`}>
              <NoticeIcon variant="info" />
              <div>
                {holderFirstName}&apos;s {missing} {missing === 'Safe Zone and WiFi' ? "aren't" : "isn't"} configured,
                so it won&apos;t carry forward. They&apos;ll set it up on their next placement — that never blocks an
                assignment.
              </div>
            </div>
          )}
        </>
      )}

      {showNoContactNotice && (
        <div className={`${styles.notice} ${styles.noticeInfo}`}>
          <NoticeIcon variant="info" />
          <div>
            Saving a household to your roster needs both an email and a phone number, and we don&apos;t have both for{' '}
            {holderFirstName}. Add them from the Fosters tab to keep this home on file.
          </div>
        </div>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleConfirm}>
          {isFoster ? 'Recall Device' : 'Unlink Device'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
